import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Stethoscope, XCircle } from "lucide-react";
import { getPaymentStatus } from "../lib/api";
import { addReview } from "../lib/reviews";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | error | timeout

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          if (res.request_id) {
            addReview({ request_id: res.request_id, question: res.question });
          }
          setStatus("paid");
          return;
        }
      } catch (e) {
        /* keep trying */
      }
      attempts += 1;
      if (attempts >= 8) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="bg-white border-2 border-slate-200 rounded-2xl p-8 text-center" data-testid="payment-success">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto" />
            <p className="mt-4 text-lg text-slate-600">Confirming your payment…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-teal-600 mx-auto" data-testid="payment-confirmed" />
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mt-4">
              Request received
            </h1>
            <p className="mt-3 text-lg text-slate-700 leading-relaxed">
              Thank you — your payment was successful. A licensed physician will review your
              question and follow up. You'll be contacted using the email you provided, if any.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-teal-700 font-semibold">
              <Stethoscope className="w-5 h-5" /> Physician review requested
            </div>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-amber-500 mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-4">
              We couldn't confirm your payment yet
            </h1>
            <p className="mt-3 text-lg text-slate-700">
              If you completed checkout, your request is still recorded. Please check back shortly.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-home-link"
          className="mt-6 inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-lg px-6 py-3 rounded-xl"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="bg-white border-2 border-slate-200 rounded-2xl p-8 text-center" data-testid="payment-cancel">
        <XCircle className="w-14 h-14 text-slate-400 mx-auto" />
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-4">
          Payment canceled
        </h1>
        <p className="mt-3 text-lg text-slate-700">
          No charge was made. You can still read your free answer any time.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-lg px-6 py-3 rounded-xl"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}
